import { AlertTriangle, ShieldAlert, Clock, CheckCircle2, Wrench, Truck, ArrowRight, Volume2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const URGENCY_META = {
  DO_NOT_DRIVE: {
    label: "Do Not Drive",
    color: "bg-red-500/15 border-red-500 text-red-300",
    icon: ShieldAlert,
    glow: "shadow-[0_0_35px_rgba(239,68,68,0.25)]",
  },
  IMMEDIATE: {
    label: "Immediate Attention",
    color: "bg-orange-500/15 border-orange-500 text-orange-300",
    icon: AlertTriangle,
    glow: "shadow-[0_0_25px_rgba(249,115,22,0.2)]",
  },
  SERVICE_SOON: {
    label: "Service Soon",
    color: "bg-yellow-500/15 border-yellow-500 text-yellow-300",
    icon: Clock,
    glow: "shadow-[0_0_20px_rgba(234,179,8,0.15)]",
  },
  LOW_RISK: {
    label: "Low Risk",
    color: "bg-emerald-500/15 border-emerald-500 text-emerald-300",
    icon: CheckCircle2,
    glow: "shadow-[0_0_20px_rgba(16,185,129,0.15)]",
  },
};

export default function DiagnosisCard({ result, onFindMechanics, onCallTow }) {
  const urgency = URGENCY_META[result.urgency] || URGENCY_META.SERVICE_SOON;
  const UrgencyIcon = urgency.icon;

  return (
    <div
      className={`rounded-2xl border border-slate-800 bg-gradient-to-br from-slate-900/90 to-slate-950/90 overflow-hidden ${urgency.glow}`}
      data-testid="diagnosis-card"
    >
      {/* Header */}
      <div className="border-l-4 border-l-amber-500 p-6 sm:p-8 border-b border-slate-800">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border ${urgency.color} mb-4`} data-testid="urgency-badge">
              <UrgencyIcon className="w-3.5 h-3.5" />
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] font-semibold">
                {urgency.label}
              </span>
            </div>
            <h2 className="font-display font-extrabold uppercase tracking-tight text-2xl sm:text-3xl lg:text-4xl text-slate-50" data-testid="diagnosis-title">
              {result.issue_title}
            </h2>
            <p className="mt-3 text-slate-300 max-w-3xl leading-relaxed" data-testid="diagnosis-summary">
              {result.summary}
            </p>
          </div>
          <div className="text-right">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
              Confidence
            </div>
            <div className="font-display text-5xl font-extrabold text-amber-500" data-testid="confidence-score">
              {result.confidence}%
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="grid md:grid-cols-2 gap-0 divide-y md:divide-y-0 md:divide-x divide-slate-800">
        {/* Affected parts */}
        <div className="p-6 sm:p-8">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-cyan-400 mb-3">
            Affected Parts & Cost Estimate
          </div>
          <div className="space-y-3">
            {(result.affected_parts || []).map((p, i) => (
              <div
                key={i}
                className="flex items-center justify-between p-3 rounded-lg bg-slate-950/50 border border-slate-800"
                data-testid={`affected-part-${i}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-100 truncate">{p.name}</div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="font-mono text-xs text-slate-400">
                      Difficulty:
                    </span>
                    <span className="text-amber-500 tracking-wide">
                      {"▲".repeat(p.difficulty)}
                      <span className="text-slate-700">
                        {"▲".repeat(Math.max(0, 5 - p.difficulty))}
                      </span>
                    </span>
                  </div>
                </div>
                <div className="text-right ml-3">
                  <div className="font-mono text-sm font-bold text-slate-50">
                    ${p.est_cost_min}–${p.est_cost_max}
                  </div>
                  <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">
                    parts + labor
                  </div>
                </div>
              </div>
            ))}
            {(!result.affected_parts || result.affected_parts.length === 0) && (
              <div className="text-slate-500 text-sm">No specific parts identified.</div>
            )}
          </div>
        </div>

        {/* Sound signature + action */}
        <div className="p-6 sm:p-8 space-y-6">
          {result.sound_signature && (
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-cyan-400 mb-3 flex items-center gap-2">
                <Volume2 className="w-3 h-3" />
                Sound Signature
              </div>
              <div className="grid grid-cols-3 gap-2">
                {Object.entries(result.sound_signature).map(([k, v]) => (
                  <div key={k} className="p-3 rounded-lg bg-slate-950/50 border border-slate-800">
                    <div className="text-[10px] font-mono uppercase text-slate-500 tracking-wider">{k}</div>
                    <div className="text-sm text-slate-100 font-semibold capitalize truncate">{v}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-cyan-400 mb-2">
              Recommended Action
            </div>
            <p className="text-slate-200 leading-relaxed" data-testid="recommended-action">
              {result.recommended_action}
            </p>
          </div>

          {result.transcription && (
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2">
                Audio Transcription
              </div>
              <p className="text-xs text-slate-400 italic leading-relaxed" data-testid="audio-transcription">
                "{result.transcription}"
              </p>
            </div>
          )}
        </div>
      </div>

      {/* CTAs */}
      <div className="p-6 sm:p-8 border-t border-slate-800 bg-slate-950/60 flex flex-wrap gap-3">
        {result.recommend_tow ? (
          <Button
            onClick={onCallTow}
            data-testid="call-tow-btn"
            className="rounded-full bg-red-600 hover:bg-red-700 text-white font-semibold gap-2 h-12 px-6 pulse-danger"
          >
            <Truck className="w-4 h-4" />
            Call Emergency Tow
            <ArrowRight className="w-4 h-4" />
          </Button>
        ) : (
          <Button
            onClick={onFindMechanics}
            data-testid="find-mechanics-btn"
            className="rounded-full bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold gap-2 h-12 px-6"
          >
            <Wrench className="w-4 h-4" />
            Find Nearby Mechanics
            <ArrowRight className="w-4 h-4" />
          </Button>
        )}
        <Button
          onClick={result.recommend_tow ? onFindMechanics : onCallTow}
          data-testid="alternate-cta-btn"
          variant="outline"
          className="rounded-full border-slate-700 bg-transparent text-slate-200 hover:bg-slate-800 hover:text-amber-400 gap-2 h-12 px-6"
        >
          {result.recommend_tow ? (
            <>
              <Wrench className="w-4 h-4" />
              Or find mechanics
            </>
          ) : (
            <>
              <Truck className="w-4 h-4" />
              Need a tow?
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
